import React, { useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import CropModal from '../components/CropModal';
import GoogleButton, { googleEnabled } from '../components/GoogleButton';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

function FieldError({ msg }) {
  if (!msg) return null;
  return <span style={{ fontSize: 12, color: '#E5503E', fontWeight: 600, marginBottom: 6, display: 'block' }}>{msg}</span>;
}

function AuthField({ label, type = 'text', placeholder, value, onChange, flex, hint, status, error }) {
  const borderColor = error ? '#E5503E' : status === 'ok' ? '#6BAA1E' : status === 'err' ? '#E5503E' : 'var(--line)';
  return (
    <div style={{ flex: flex ? 1 : undefined, minWidth: 0 }}>
      <FieldError msg={error} />
      <div className="card col gap-2" style={{ padding: 14, borderColor }}>
        <span className="mono" style={{ color: 'var(--text-3)' }}>{label}</span>
        <input
          type={type}
          placeholder={placeholder}
          value={value}
          onChange={onChange}
          style={{
            flex: 1, background: 'transparent', border: 0, outline: 'none', minWidth: 0,
            color: 'var(--text)', fontSize: 14, fontFamily: 'inherit',
          }}
        />
        {hint && (
          <span style={{
            fontSize: 11, marginTop: 2,
            color: status === 'ok' ? '#6BAA1E' : status === 'err' ? '#E5503E' : 'var(--text-3)',
          }}>{hint}</span>
        )}
      </div>
    </div>
  );
}

function Register({ onLogin }) {
  const [email, setEmail]                     = useState('');
  const [password, setPassword]               = useState('');
  const [firstName, setFirstName]             = useState('');
  const [lastName, setLastName]               = useState('');
  // phone kept as empty string for API compat
  const [phone] = useState('');
  const [gender, setGender]                   = useState('');
  const [level, setLevel]                     = useState('');
  const [birthdate, setBirthdate]             = useState('');
  const [position, setPosition]               = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [avatarBase64, setAvatarBase64]       = useState(null);
  const [cropSrc, setCropSrc]                 = useState(null);
  const [error, setError]                     = useState('');
  const [errs, setErrs]                       = useState({});
  const [loading, setLoading]                 = useState(false);
  const clearE = (k) => setErrs(p => { if (!p[k]) return p; const n = { ...p }; delete n[k]; return n; });

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const inviteCode = searchParams.get('code');

  const pwMatch    = password.length > 0 && password === confirmPassword;
  const pwMismatch = confirmPassword.length > 0 && password !== confirmPassword;

  const handleAvatarChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const url = URL.createObjectURL(file);
    setCropSrc(url);
    e.target.value = '';
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setError('');
    // Validation par champ : chaque message s'affiche au-dessus du champ concerné.
    const er = {};
    if (!firstName.trim()) er.firstName = 'Indique ton prénom.';
    if (!lastName.trim()) er.lastName = 'Indique ton nom.';
    if (!email.trim()) er.email = 'Indique ton e-mail.';
    else if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) er.email = 'E-mail invalide.';
    if (!gender) er.gender = 'Sélectionne ton genre.';
    if (!level) er.level = 'Sélectionne ton niveau.';
    if (password.length < 6) er.password = 'Au moins 6 caractères.';
    if (confirmPassword !== password) er.confirmPassword = 'Les mots de passe ne correspondent pas.';
    setErrs(er);
    if (Object.keys(er).length > 0) return;
    setLoading(true);
    try {
      const fn = firstName.trim();
      const ln = lastName.trim();
      const normalFirst = fn.charAt(0).toUpperCase() + fn.slice(1).toLowerCase();
      const normalLast  = ln.toUpperCase();
      const res = await axios.post(`${API_URL}/api/auth/register`, {
        email, password,
        firstName: normalFirst, lastName: normalLast,
        gender, level, phone, avatarUrl: avatarBase64 || null,
        position: position || null, birthdate: birthdate || null,
      });
      localStorage.removeItem('onboarding_done');
      onLogin(res.data.token, res.data.user);
      navigate(inviteCode ? `/rejoindre/${inviteCode}` : '/dashboard');
    } catch (err) {
      setError(err.response?.data?.error || "Erreur lors de l'inscription");
    } finally {
      setLoading(false);
    }
  };

  const handleGoogle = async (credential) => {
    setError('');
    try {
      const res = await axios.post(`${API_URL}/api/auth/google`, { credential });
      onLogin(res.data.token, res.data.user);
      navigate(res.data.profileComplete ? (inviteCode ? `/rejoindre/${inviteCode}` : '/dashboard') : '/completer-profil');
    } catch (err) {
      setError(err.response?.data?.error || "Erreur lors de l'inscription");
    }
  };

  const LEVELS = [
    ['loisir', 'Loisir'],
    ['departemental', 'Dép.'],
    ['regional', 'Régio.'],
    ['national', 'Nat.'],
    ['pro', 'Pro'],
  ];

  return (
    <>
      {cropSrc && (
        <CropModal
          src={cropSrc}
          onConfirm={(b64) => { setAvatarBase64(b64); URL.revokeObjectURL(cropSrc); setCropSrc(null); }}
          onCancel={() => { URL.revokeObjectURL(cropSrc); setCropSrc(null); }}
        />
      )}

      <div className="screen" style={{ background: 'var(--ink)', minHeight: '100vh' }}>
        <div style={{ height: 54 }} />

        {/* Sub-header */}
        <div style={{ padding: '4px 20px 12px', flexShrink: 0, borderBottom: '1px solid var(--line)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Link to="/login" aria-label="Retour" style={{
              width: 36, height: 36, borderRadius: '50%', background: 'var(--ink-3)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              textDecoration: 'none', color: 'var(--text)',
            }}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M19 12H5M11 6l-6 6 6 6"/>
              </svg>
            </Link>
            <div className="display" style={{ fontSize: 18 }}>Inscription</div>
            <div style={{ width: 36 }} />
          </div>
        </div>

        <div style={{ overflowY: 'auto', paddingBottom: 120 }}>

          {/* Avatar picker */}
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginTop: 16, padding: '0 20px' }}>
            <label style={{ cursor: 'pointer', position: 'relative' }}>
              <div style={{
                width: 96, height: 96, borderRadius: '50%',
                background: avatarBase64 ? 'transparent' : 'linear-gradient(160deg, #F5A623, #E5503E)',
                display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden',
              }}>
                {avatarBase64 ? (
                  <img src={avatarBase64} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                ) : (
                  <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.85)" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="9" r="3.6" />
                    <path d="M4.5 19.5c1.4-3 4.4-4.5 7.5-4.5s6.1 1.5 7.5 4.5" />
                  </svg>
                )}
              </div>
              <span style={{
                position: 'absolute', bottom: 0, right: 0, width: 30, height: 30, borderRadius: '50%',
                background: 'var(--sun)', color: 'var(--ink)', fontSize: 18, fontWeight: 800,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                border: '3px solid var(--ink)',
              }}>+</span>
              <input type="file" accept="image/*" style={{ display: 'none' }} onChange={handleAvatarChange} />
            </label>
            <span className="mono" style={{ color: 'var(--text-3)', marginTop: 8 }}>Photo de profil</span>
          </div>

          {error && (
            <div style={{
              margin: '16px 20px 0', padding: '10px 14px', borderRadius: 10,
              background: 'rgba(229,80,62,0.1)', color: '#E5503E',
              fontSize: 13, fontWeight: 600, border: '1px solid rgba(229,80,62,0.25)',
            }}>{error}</div>
          )}

          <form onSubmit={handleRegister}>

            {/* Identité */}
            <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', padding: '0 20px', margin: '22px 0 12px' }}>
              <h3 style={{ fontSize: 13, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--text-2)', margin: 0 }}>Identité</h3>
            </div>
            <div style={{ padding: '0 20px', display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div style={{ display: 'flex', gap: 10 }}>
                <AuthField label="Prénom" placeholder="Nina" flex value={firstName} error={errs.firstName} onChange={e => { setFirstName(e.target.value); clearE('firstName'); }} />
                <AuthField label="Nom" placeholder="Bertin" flex value={lastName} error={errs.lastName} onChange={e => { setLastName(e.target.value); clearE('lastName'); }} />
              </div>
              <div>
                <FieldError msg={errs.email} />
                <div className="card col gap-2" style={{ padding: 14, borderColor: errs.email ? '#E5503E' : 'var(--line)' }}>
                  <span className="mono" style={{ color: 'var(--text-3)' }}>E-mail</span>
                  <input type="email" placeholder="nina@spike.fr" value={email} onChange={e => { setEmail(e.target.value); clearE('email'); }}
                    style={{ flex: 1, background: 'transparent', border: 0, outline: 'none', minWidth: 0, color: 'var(--text)', fontSize: 14, fontFamily: 'inherit' }} />
                </div>
              </div>
            </div>

            {/* Genre */}
            <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', padding: '0 20px', margin: '22px 0 12px' }}>
              <h3 style={{ fontSize: 13, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--text-2)', margin: 0 }}>Genre</h3>
            </div>
            <div style={{ padding: '0 20px' }}>
              <FieldError msg={errs.gender} />
              <div className="card col gap-2" style={{ padding: 14, borderColor: errs.gender ? '#E5503E' : 'var(--line)' }}>
                <div style={{ display: 'flex', gap: 8 }}>
                  {/* ponytail: pas de « Autre » — le backend et les règles de parité mixte ne
                      connaissent que masculin/feminin ; le proposer revenait à le refuser après coup. */}
                  {[['masculin', 'Homme'], ['feminin', 'Femme']].map(([k, l]) => (
                    <button key={k} type="button" onClick={() => { setGender(k); clearE('gender'); }} style={{
                      flex: 1, padding: '10px 0', borderRadius: 8, fontSize: 13, fontWeight: 600,
                      background: gender === k ? 'var(--text)' : 'var(--ink-3)',
                      color: gender === k ? 'var(--ink)' : 'var(--text-2)',
                    }}>{l}</button>
                  ))}
                </div>
              </div>
            </div>

            {/* Niveau */}
            <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', padding: '0 20px', margin: '22px 0 12px' }}>
              <h3 style={{ fontSize: 13, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--text-2)', margin: 0 }}>Niveau</h3>
            </div>
            <div style={{ padding: '0 20px' }}>
              <FieldError msg={errs.level} />
              <div className="card col gap-2" style={{ padding: 14, borderColor: errs.level ? '#E5503E' : 'var(--line)' }}>
                <div style={{ display: 'flex', gap: 6 }}>
                  {LEVELS.map(([val, lbl]) => (
                    <button key={val} type="button" onClick={() => { setLevel(val); clearE('level'); }} style={{
                      flex: 1, minWidth: 0, padding: '10px 0', borderRadius: 8, fontSize: 12, fontWeight: 600,
                      background: level === val ? 'var(--sun)' : 'var(--ink-3)',
                      color: level === val ? 'var(--ink)' : 'var(--text-2)',
                    }}>{lbl}</button>
                  ))}
                </div>
                <span style={{ fontSize: 11, color: 'var(--text-3)', marginTop: 2 }}>
                  Modifiable plus tard. Sert à matcher partenaires et tournois.
                </span>
              </div>
            </div>

            {/* Date de naissance */}
            <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', padding: '0 20px', margin: '22px 0 12px' }}>
              <h3 style={{ fontSize: 13, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--text-2)', margin: 0 }}>Date de naissance</h3>
            </div>
            <div style={{ padding: '0 20px' }}>
              <div className="card col gap-2" style={{ padding: 14 }}>
                <span className="mono" style={{ color: 'var(--text-3)' }}>Naissance</span>
                <input type="date" value={birthdate} onChange={e => setBirthdate(e.target.value)}
                  style={{ flex: 1, background: 'transparent', border: 0, outline: 'none', minWidth: 0, color: 'var(--text)', fontSize: 14, fontFamily: 'inherit' }} />
                <span style={{ fontSize: 11, color: 'var(--text-3)', marginTop: 2 }}>
                  Optionnel — utilisé pour vérifier l'âge des catégories jeunes.
                </span>
              </div>
            </div>

            {/* Poste */}
            <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', padding: '0 20px', margin: '22px 0 12px' }}>
              <h3 style={{ fontSize: 13, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--text-2)', margin: 0 }}>Poste préféré</h3>
            </div>
            <div style={{ padding: '0 20px' }}>
              <div className="card col gap-2" style={{ padding: 14 }}>
                <div style={{ display: 'flex', gap: 8 }}>
                  {[['', 'Peu importe'], ['passeur', 'Passeur'], ['attaquant', 'Attaquant']].map(([val, lbl]) => (
                    <button key={val} type="button" onClick={() => setPosition(val)} style={{
                      flex: 1, padding: '10px 0', borderRadius: 8, fontSize: 13, fontWeight: 600,
                      background: position === val ? 'var(--text)' : 'var(--ink-3)',
                      color: position === val ? 'var(--ink)' : 'var(--text-2)',
                    }}>{lbl}</button>
                  ))}
                </div>
              </div>
            </div>

            {/* Mot de passe */}
            <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', padding: '0 20px', margin: '22px 0 12px' }}>
              <h3 style={{ fontSize: 13, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--text-2)', margin: 0 }}>Mot de passe</h3>
            </div>
            <div style={{ padding: '0 20px', display: 'flex', flexDirection: 'column', gap: 12 }}>
              <AuthField label="Mot de passe" placeholder="6 caractères min." type="password"
                value={password} error={errs.password} onChange={e => { setPassword(e.target.value); clearE('password'); }} />
              <AuthField label="Confirmer le mot de passe" placeholder="Retape ton mot de passe" type="password"
                value={confirmPassword} error={errs.confirmPassword} onChange={e => { setConfirmPassword(e.target.value); clearE('confirmPassword'); }}
                status={pwMatch ? 'ok' : pwMismatch ? 'err' : null}
                hint={pwMatch ? 'Mots de passe identiques' : pwMismatch ? 'Les mots de passe ne correspondent pas' : null}
              />
            </div>

            <div style={{ padding: '16px 20px 0' }}>
              <p style={{ fontSize: 11, color: 'var(--text-3)', lineHeight: 1.5, textAlign: 'center', margin: 0 }}>
                En créant un compte tu acceptes les <span style={{ color: 'var(--text-2)' }}>conditions</span> et la <span style={{ color: 'var(--text-2)' }}>politique de confidentialité</span> de Spike.
              </p>
            </div>

          </form>

          {googleEnabled && (
            <>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, margin: '20px 20px 0' }}>
                <div style={{ flex: 1, height: 1, background: 'var(--line)' }} />
                <span style={{ color: 'var(--text-3)', fontSize: 12 }}>ou</span>
                <div style={{ flex: 1, height: 1, background: 'var(--line)' }} />
              </div>
              <div style={{ padding: '12px 20px 0' }}>
                <GoogleButton onCredential={handleGoogle} text="signup_with" />
              </div>
            </>
          )}

          <div style={{ display: 'flex', justifyContent: 'center', marginTop: 28, gap: 6 }}>
            <span style={{ color: 'var(--text-3)', fontSize: 13 }}>Déjà inscrit ?</span>
            <Link to="/login" style={{ color: 'var(--sun)', fontSize: 13, fontWeight: 700, textDecoration: 'none' }}>
              Connexion
            </Link>
          </div>
        </div>

        {/* Fixed submit button */}
        <div style={{
          position: 'fixed', bottom: 0, left: 0, right: 0, padding: '12px 20px 28px',
          background: 'linear-gradient(to top, var(--ink) 70%, transparent)',
        }}>
          <button onClick={handleRegister} disabled={loading} className="btn btn-primary"
            style={{ width: '100%', padding: '15px 20px' }}>
            {loading ? 'Création en cours…' : 'Créer mon compte'}
          </button>
        </div>
      </div>
    </>
  );
}

export default Register;
